import { AppFooter } from "../components/AppFooter";
import { useNotifications } from "../hooks/useNotifications";
import { NOTIFICATION_CATEGORIES, type NotificationCategory } from "../types";

const LABELS: Record<NotificationCategory, { title: string; sub: string }> = {
  mentions: { title: "Erwähnungen", sub: "Wenn du mit @Name in einem Kommentar erwähnt wirst" },
  calendar: { title: "Kalender", sub: "Neue Termine" },
  calendarComments: { title: "Termin-Kommentare", sub: "Neuer Kommentar zu einem Termin" },
  loveNote: { title: "Kleine Nachrichten", sub: "Wenn dir eine Nachricht geschickt wird" },
  lists: { title: "Listen", sub: "Neue Liste erstellt" },
  listItems: { title: "Listen-Einträge", sub: "Neuer Eintrag in einer Liste" },
  todos: { title: "Todos", sub: "Neues Todo" },
  todoComments: { title: "Todo-Kommentare", sub: "Neuer Kommentar zu einem Todo" },
  importantDates: { title: "Wichtige Daten", sub: "Neuer Eintrag" },
  notes: { title: "Notizen", sub: "Neue Notiz" },
  links: { title: "Links", sub: "Neue Sammlung oder neuer Link" },
};

export function Notifications() {
  const { supported, permission, enabled, prefs, busy, error, enable, disable, setPref } = useNotifications();

  return (
    <main className="mx-auto min-h-dvh max-w-md px-5 pb-28 pt-6">
      <h1 className="font-display text-[1.75rem] font-semibold tracking-tight text-plum-900 dark:text-night-accent">Benachrichtigungen</h1>
      <p className="mt-1 text-sm text-ink-400 dark:text-night-faint">Push-Mitteilungen für dieses Gerät.</p>

      {!supported ? (
        <div className="card mt-5 rounded-2xl p-4">
          <p className="text-sm text-ink-600 dark:text-night-muted">
            Dieser Browser unterstützt keine Push-Benachrichtigungen. Auf dem iPhone geht das nur, wenn die App vorher
            aufs Homescreen installiert wurde.
          </p>
        </div>
      ) : (
        <>
          <div className="card mt-5 rounded-2xl p-4">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="font-semibold text-ink-900 dark:text-night-text">{enabled ? "Aktiviert" : "Nicht aktiviert"}</p>
                <p className="mt-0.5 text-xs text-ink-400 dark:text-night-faint">
                  {enabled ? "Dieses Gerät bekommt Push-Mitteilungen." : "Für dieses Gerät noch nicht eingerichtet."}
                </p>
              </div>
              <button
                onClick={enabled ? disable : enable}
                disabled={busy || permission === "denied"}
                className={`h-10 flex-shrink-0 rounded-xl px-4 text-sm font-semibold disabled:opacity-50 ${
                  enabled ? "border border-cream-200 text-ink-900 dark:border-night-border dark:text-night-text" : "bg-plum-700 text-cream-50"
                }`}
              >
                {busy ? "…" : enabled ? "Deaktivieren" : "Aktivieren"}
              </button>
            </div>
            {permission === "denied" && (
              <p className="mt-3 text-sm text-garnet-600">
                Benachrichtigungen sind für diese Seite blockiert. Das lässt sich nur in den Browser- oder
                Handy-Einstellungen wieder erlauben.
              </p>
            )}
            {error && permission !== "denied" && <p className="mt-3 text-sm text-garnet-600">{error}</p>}
          </div>

          <section className="mt-6">
            <h2 className="text-sm font-semibold text-ink-900 dark:text-night-text">Wofür</h2>
            <p className="mt-1 text-xs text-ink-400 dark:text-night-faint">Gilt, sobald Benachrichtigungen oben aktiviert sind.</p>
            <ul className="mt-3 divide-y divide-cream-100 rounded-2xl bg-white shadow-card dark:divide-night-border dark:border dark:border-night-border dark:bg-night-surface dark:shadow-none">
              {NOTIFICATION_CATEGORIES.map((cat) => {
                const on = prefs[cat] !== false;
                return (
                  <li key={cat} className="flex items-center justify-between gap-3 px-4 py-3.5">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-ink-900 dark:text-night-text">{LABELS[cat].title}</p>
                      <p className="mt-0.5 text-xs text-ink-400 dark:text-night-faint">{LABELS[cat].sub}</p>
                    </div>
                    <button
                      onClick={() => setPref(cat, !on)}
                      aria-pressed={on}
                      className={`relative h-7 w-12 flex-shrink-0 rounded-full transition-colors ${on ? "bg-plum-700" : "bg-cream-200 dark:bg-night-border"}`}
                    >
                      <span
                        className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow transition-[left] ${on ? "left-6" : "left-1"}`}
                      />
                    </button>
                  </li>
                );
              })}
            </ul>
          </section>
        </>
      )}

      <AppFooter />
    </main>
  );
}
